import { useCallback, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  FilterBar,
  GridLoading,
  ResponsiveDataView,
  StatusBadge,
  statusCellStyle,
  workflowStatusColors,
} from '@/components/common';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { statusCodeToLabel } from '@/pages/fert-work/utils/statusHelpers';
import { useSites } from '@/services/sites';

export type SessionsGridProps = {
  sessions: any[];
  isLoading?: boolean;
  onRowDoubleClick?: (sessionId: string, date: string, siteId: string) => void;
  onFilterChange?: (filters: {
    siteId?: string | null;
    date?: string | null;
    status?: string | null;
    createdById?: string | null;
  }) => void;
};

type SessionRow = {
  id: string;
  sessionId: string;
  date: string;
  site: string;
  status: number;
  siteId: string;
  sessionType: number;
};

const statusOptions = [
  { label: 'Validating', value: 0 },
  { label: 'In Progress', value: 1 },
  { label: 'Issues', value: 4 },
  { label: 'Complete', value: 2 },
  { label: 'Error', value: 3 },
];

const SessionCard = ({ session }: { session: SessionRow }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <div className="mb-2 flex items-start justify-between">
      <div>
        <div className="font-semibold text-gray-900">{session.site}</div>
        <div className="text-sm text-gray-500">{session.date}</div>
      </div>
      <StatusBadge
        label={statusCodeToLabel(session.status)}
        colorMap={workflowStatusColors}
      />
    </div>
  </div>
);

export const SessionsGrid = ({
  sessions,
  isLoading,
  onRowDoubleClick,
  onFilterChange,
}: SessionsGridProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnDefs = useMemo(
    () => [
      {
        field: 'date',
        headerName: t('fertWork.sessionDate', 'Session Date'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'site',
        headerName: t('fertWork.site', 'Site'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'sessionType',
        headerName: t('fertWork.sessionType', 'Session Type'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) =>
          params.value === 1 ? 'Commercial' : 'Conv / SF',
      },
      {
        field: 'status',
        headerName: t('fertWork.status', 'Status'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) => statusCodeToLabel(params.value),
        cellStyle: statusCellStyle(workflowStatusColors, statusCodeToLabel),
      },
    ],
    [t],
  );

  const [filterDate, setFilterDate] = useState<Date | null>(null);
  const [filterSiteId, setFilterSiteId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | null>(null);

  const { data: fertilizationSites } = useSites({ filter: 'fertilization' });

  const emitFilterChange = useCallback(
    (
      overrides: Partial<{
        siteId: string | null;
        date: string | null;
        status: string | null;
      }>,
    ) => {
      const merged = {
        siteId:
          overrides.siteId !== undefined ? overrides.siteId : filterSiteId,
        date:
          overrides.date !== undefined
            ? overrides.date
            : filterDate
              ? format(filterDate, 'yyyy-MM-dd')
              : null,
        status:
          overrides.status !== undefined
            ? overrides.status
            : filterStatus !== null
              ? String(filterStatus)
              : null,
      };
      onFilterChange?.(merged);
    },
    [filterSiteId, filterDate, filterStatus, onFilterChange],
  );

  const rowData = useMemo(() => {
    return sessions.map((session) => ({
      id: session.id,
      sessionId: session.sessionId,
      date: format(new Date(session.createdAt), 'yyyy-MM-dd'),
      site: session.siteName,
      siteId: session.siteId,
      status: session.status,
      sessionType: session.sessionType ?? 0,
    }));
  }, [sessions]);

  const handleClearFilters = () => {
    setFilterDate(null);
    setFilterSiteId(null);
    setFilterStatus(null);
    onFilterChange?.({ siteId: null, date: null, status: null });
  };

  const handleRowDoubleClick = (event: any) => {
    const session = event.data;
    if (onRowDoubleClick) {
      onRowDoubleClick(session.id, session.date, session.siteId);
    } else {
      navigate(
        `/fert-work/session?sessionId=${session.id}&date=${session.date}&siteId=${session.siteId}`,
      );
    }
  };

  const handleCardClick = (session: SessionRow) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(session.id, session.date, session.siteId);
    } else {
      navigate(
        `/fert-work/session?sessionId=${session.id}&date=${session.date}&siteId=${session.siteId}`,
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-end gap-1">
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-10 animate-pulse rounded bg-gray-200" />
        </div>
        <GridLoading
          label={t('fertWork.loadingSessions', 'Loading sessions...')}
          className="h-[600px]"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <FilterBar
        onClear={handleClearFilters}
        clearTooltip={t('fertWork.clearFilters', 'Clear Filters')}
      >
        <Select
          items={(fertilizationSites ?? []).map((site) => ({
            label: site.name,
            value: site.id,
          }))}
          value={filterSiteId}
          onChange={(value) => {
            setFilterSiteId(value);
            emitFilterChange({ siteId: value ?? null });
          }}
        >
          <SelectTrigger className="w-full md:w-48">
            <SelectValue
              placeholder={t('fertWork.filterBySite', 'Filter by site')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={null}>{t('common.all', 'All')}</SelectItem>
            {(fertilizationSites ?? []).map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DatePicker
          value={filterDate ?? undefined}
          onChange={(date) => {
            const val = date ?? null;
            setFilterDate(val);
            emitFilterChange({ date: val ? format(val, 'yyyy-MM-dd') : null });
          }}
          placeholder={t('fertWork.filterByDate', 'Filter by date')}
          className="h-8 w-full md:w-48"
        />

        <Select
          items={statusOptions}
          value={filterStatus}
          onChange={(value) => {
            setFilterStatus(value);
            emitFilterChange({
              status:
                value !== null && value !== undefined ? String(value) : null,
            });
          }}
        >
          <SelectTrigger className="w-full md:w-48">
            <SelectValue
              placeholder={t('fertWork.filterByStatus', 'Filter by status')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={null}>{t('common.all', 'All')}</SelectItem>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>

      <ResponsiveDataView<SessionRow>
        rowData={rowData}
        columnDefs={columnDefs}
        storageKey="fert-work-grid"
        getRowId={(row) => row.id}
        paginationPageSize={100}
        paginationPageSizeSelector={[20, 50, 100, 250, 500, 1000]}
        renderCard={(session) => <SessionCard session={session} />}
        emptyMessage={t('fertWork.noSessionsFound', 'No sessions found')}
        onRowDoubleClicked={handleRowDoubleClick}
        onCardClick={handleCardClick}
      />
    </div>
  );
};
